import { useEffect, useState, useCallback } from "react";
import { DndProvider, useDrag, useDrop } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { useTicketStore } from "@store/ticketStore";
import type { DevTicket, DevTicketStatus } from "types/dev_ticket";
import Icon1 from "@assets/icons/Icon";
import { colors } from "sexy";

const COLUMNS: { status: DevTicketStatus; label: string; color: string; bg: string }[] = [
  { status: "todo", label: "À traiter", color: "border-orange-300", bg: "bg-orange-50" },
  { status: "in_progress", label: "En cours", color: "border-blue-300", bg: "bg-blue-50" },
  { status: "done", label: "Terminé", color: "border-green-300", bg: "bg-green-50" },
];

const ITEM_TYPE = "TICKET";

function TicketCard({ ticket, onDelete }: { ticket: DevTicket; onDelete: (id: string) => void }) {
  const [{ isDragging }, drag] = useDrag(() => ({
    type: ITEM_TYPE,
    item: { id: ticket.id },
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  }), [ticket.id]);

  const [expanded, setExpanded] = useState(false);
  const firstAttachment = ticket.attachments?.[0];
  const isImage = firstAttachment && firstAttachment.type?.startsWith("image/");

  const date = ticket.created_at
    ? new Date(ticket.created_at).toLocaleDateString("fr-FR", {
        day: "numeric",
        month: "short",
      })
    : "";

  return (
    <div
      ref={drag as any}
      className={`bg-white rounded-lg border border-neutral-200 p-3 cursor-grab active:cursor-grabbing shadow-sm hover:shadow-md transition-shadow ${
        isDragging ? "opacity-40" : ""
      }`}
    >
      <div className="flex flex-row justify-between items-start gap-2">
        <div className="flex-1 min-w-0">
          <div
            className="text-sm font-medium text-neutral-800 cursor-pointer"
            onClick={() => setExpanded(!expanded)}
          >
            {ticket.title}
          </div>
          {expanded && ticket.description && ticket.description !== ticket.title && (
            <div className="text-xs text-neutral-500 mt-1 whitespace-pre-wrap">
              {ticket.description}
            </div>
          )}
        </div>
        <button
          onClick={() => onDelete(ticket.id)}
          className="text-neutral-300 hover:text-red-400 transition-colors flex-shrink-0"
        >
          <Icon1 name="close" size={14} color="currentColor" />
        </button>
      </div>

      {isImage && (
        <a href={firstAttachment.url} target="_blank" rel="noreferrer">
          <img
            src={firstAttachment.url}
            alt={firstAttachment.name}
            className="mt-2 rounded-md w-full max-h-32 object-cover border border-neutral-100"
          />
        </a>
      )}

      <div className="flex flex-row items-center gap-2 mt-2">
        {ticket.source_ticket_bug_id && (
          <span className="text-xs px-1.5 py-0.5 rounded bg-neutral-100 text-neutral-500">
            #{ticket.source_ticket_bug_id}
          </span>
        )}
        {ticket.sender_name && (
          <span className="text-xs text-neutral-400">{ticket.sender_name}</span>
        )}
        <span className="text-xs text-neutral-300 ml-auto">{date}</span>
      </div>
    </div>
  );
}

function TicketColumn({
  status,
  label,
  color,
  bg,
  tickets,
  onDrop,
  onDelete,
}: {
  status: DevTicketStatus;
  label: string;
  color: string;
  bg: string;
  tickets: DevTicket[];
  onDrop: (ticketId: string, status: DevTicketStatus) => void;
  onDelete: (id: string) => void;
}) {
  const [{ isOver }, drop] = useDrop(() => ({
    accept: ITEM_TYPE,
    drop: (item: { id: string }) => onDrop(item.id, status),
    collect: (monitor) => ({ isOver: monitor.isOver() }),
  }), [status, onDrop]);

  return (
    <div
      ref={drop as any}
      className={`flex-1 min-w-64 flex flex-col rounded-xl border-t-4 ${color} ${
        isOver ? "bg-neutral-100" : bg
      } transition-colors`}
    >
      <div className="flex flex-row items-center gap-2 px-4 py-3">
        <span className="text-sm font-semibold text-neutral-700">{label}</span>
        <span className="text-xs px-2 py-0.5 rounded-full bg-white text-neutral-500 border border-neutral-200">
          {tickets.length}
        </span>
      </div>
      <div className="flex flex-col gap-2 px-3 pb-3 overflow-y-auto" style={{ maxHeight: "calc(100vh - 160px)" }}>
        {tickets.map((t) => (
          <TicketCard key={t.id} ticket={t} onDelete={onDelete} />
        ))}
        {tickets.length === 0 && (
          <div className="text-xs text-neutral-400 text-center py-8">
            Aucun ticket
          </div>
        )}
      </div>
    </div>
  );
}

export default function TicketsScreen() {
  const tickets = useTicketStore((s) => s.tickets);
  const isLoading = useTicketStore((s) => s.isLoading);
  const fetchTickets = useTicketStore((s) => s.fetchTickets);
  const updateTicketStatus = useTicketStore((s) => s.updateTicketStatus);
  const deleteTicket = useTicketStore((s) => s.deleteTicket);

  useEffect(() => {
    fetchTickets();
  }, []);

  const handleDrop = useCallback(
    (ticketId: string, newStatus: DevTicketStatus) => {
      const ticket = tickets.find((t) => t.id === ticketId);
      if (ticket && ticket.status !== newStatus) {
        updateTicketStatus(ticketId, newStatus);
      }
    },
    [tickets, updateTicketStatus]
  );

  const handleDelete = useCallback(
    (ticketId: string) => {
      deleteTicket(ticketId);
    },
    [deleteTicket]
  );

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="flex flex-col overflow-auto max-h-screen">
        <div className="flex flex-col h-fit pb-32 mt-10 mx-4 lg:ml-10">
          <div className="flex flex-row items-center gap-3 mb-6">
            <Icon1 name="task" size={28} color={colors.neutral_400} />
            <div>
              <h1 className="text-xl font-bold text-neutral-800">Tickets</h1>
              <p className="text-sm text-neutral-400">
                Glisser-déposer pour changer le statut
              </p>
            </div>
            <button
              onClick={() => fetchTickets()}
              className="ml-auto px-3 py-1.5 text-xs font-medium bg-neutral-100 hover:bg-neutral-200 text-neutral-600 rounded-lg transition-colors"
            >
              Rafraîchir
            </button>
          </div>

          {isLoading && tickets.length === 0 ? (
            <div className="flex items-center justify-center py-20 text-neutral-400 text-sm">
              Chargement...
            </div>
          ) : (
            <div className="flex flex-row gap-4">
              {COLUMNS.map((col) => (
                <TicketColumn
                  key={col.status}
                  status={col.status}
                  label={col.label}
                  color={col.color}
                  bg={col.bg}
                  tickets={tickets.filter((t) => t.status === col.status)}
                  onDrop={handleDrop}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </DndProvider>
  );
}
